import { Component } from '@theme/component';

/**
 * @typedef {Object} SfAbSliderRefs
 * @property {HTMLElement} track - Scroll-snap track holding the slides
 * @property {HTMLElement[]} [slides] - Slide elements
 * @property {HTMLButtonElement} [prev] - Previous arrow
 * @property {HTMLButtonElement} [next] - Next arrow
 * @property {HTMLElement} [dots] - Dot container (populated here)
 * @property {HTMLButtonElement} [pause] - Autoplay pause toggle
 * @property {HTMLElement} [arrows] - Arrow container
 */

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

/**
 * Scroll-snap slider shared by the AB test sections (hero, product carousel,
 * quote row, category row). Native scrolling does the work, so it degrades to a
 * plain swipeable row without JS and is keyboard-operable for free.
 *
 * @extends {Component<SfAbSliderRefs>}
 */
class SfAbSlider extends Component {
  /** @type {number} */
  #index = 0;
  /** @type {number} */
  #autoplayMs = 0;
  /** @type {boolean} */
  #paused = false;
  /** @type {number} */
  #timer = 0;
  /** @type {number} */
  #scrollDebounce = 0;
  /** @type {boolean} */
  #reducedMotion = false;
  /** @type {HTMLButtonElement[]} */
  #dots = [];
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  connectedCallback() {
    super.connectedCallback();

    const { track } = this.refs;
    if (!track) return;

    this.#reducedMotion = window.matchMedia(REDUCED_MOTION_QUERY).matches;
    this.#autoplayMs = (parseFloat(this.dataset.autoplay || '0') || 0) * 1000;

    this.#buildDots();

    track.addEventListener('scroll', this.#onScroll, { passive: true });
    this.refs.prev?.addEventListener('click', this.#onPrev);
    this.refs.next?.addEventListener('click', this.#onNext);
    this.refs.pause?.addEventListener('click', this.#togglePause);

    this.addEventListener('pointerenter', this.#suspend);
    this.addEventListener('pointerleave', this.#resume);
    this.addEventListener('focusin', this.#suspend);
    this.addEventListener('focusout', this.#resume);
    document.addEventListener('visibilitychange', this.#onVisibilityChange);

    this.#resizeObserver = new ResizeObserver(() => this.#syncControls());
    this.#resizeObserver.observe(track);

    this.refs.arrows?.removeAttribute('hidden');
    this.#syncControls();
    this.#startAutoplay();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.track?.removeEventListener('scroll', this.#onScroll);
    this.refs.prev?.removeEventListener('click', this.#onPrev);
    this.refs.next?.removeEventListener('click', this.#onNext);
    this.refs.pause?.removeEventListener('click', this.#togglePause);
    this.removeEventListener('pointerenter', this.#suspend);
    this.removeEventListener('pointerleave', this.#resume);
    this.removeEventListener('focusin', this.#suspend);
    this.removeEventListener('focusout', this.#resume);
    document.removeEventListener('visibilitychange', this.#onVisibilityChange);
    this.#resizeObserver?.disconnect();
    this.#stopAutoplay();
    clearTimeout(this.#scrollDebounce);
  }

  /* ---------- geometry ---------- */

  get #slideElements() {
    return /** @type {HTMLElement[]} */ (Array.from(this.refs.track?.children ?? []));
  }

  get #unit() {
    const first = this.#slideElements[0];
    if (!first) return 0;
    const gap = parseFloat(getComputedStyle(/** @type {HTMLElement} */ (this.refs.track)).columnGap) || 0;
    return first.getBoundingClientRect().width + gap;
  }

  get #perView() {
    const track = this.refs.track;
    const unit = this.#unit;
    if (!track || !unit) return 1;
    return Math.max(1, Math.round(track.clientWidth / unit));
  }

  get #pageCount() {
    return Math.max(1, this.#slideElements.length - this.#perView + 1);
  }

  get #maxScroll() {
    const track = this.refs.track;
    return track ? track.scrollWidth - track.clientWidth : 0;
  }

  /* ---------- controls ---------- */

  #buildDots() {
    const host = this.refs.dots;
    if (!host) return;
    host.textContent = '';
    if (this.#slideElements.length <= 1) return;

    this.#dots = Array.from({ length: this.#pageCount }, (_, i) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'sf-ab-slider__dot';
      dot.setAttribute('aria-label', `${this.dataset.dotLabel || 'Go to slide'} ${i + 1}`);
      dot.addEventListener('click', () => this.#goTo(i));
      host.append(dot);
      return dot;
    });
  }

  #syncControls = () => {
    const { prev, next, arrows, dots, pause } = this.refs;
    const track = this.refs.track;
    if (!track) return;

    if (this.#dots.length && this.#dots.length !== this.#pageCount) this.#buildDots();

    const single = this.#maxScroll <= 1;
    const loop = this.dataset.loop === 'true';

    if (prev) prev.disabled = !loop && track.scrollLeft <= 1;
    if (next) next.disabled = !loop && track.scrollLeft >= this.#maxScroll - 1;
    if (arrows) arrows.hidden = single;
    if (dots) dots.hidden = single;
    if (pause) pause.hidden = single || !this.#autoplayMs;

    this.#dots.forEach((dot, i) => {
      dot.setAttribute('aria-current', i === this.#index ? 'true' : 'false');
    });
  };

  #onScroll = () => {
    clearTimeout(this.#scrollDebounce);
    this.#scrollDebounce = setTimeout(() => {
      const unit = this.#unit;
      const track = this.refs.track;
      this.#index = unit && track ? Math.min(Math.round(track.scrollLeft / unit), this.#pageCount - 1) : 0;
      this.#syncControls();
    }, 80);
  };

  /** @param {number} index */
  #goTo(index) {
    const slides = this.#slideElements;
    const track = this.refs.track;
    const target = slides[Math.max(0, Math.min(index, slides.length - 1))];
    if (!target || !track) return;

    this.#index = index;
    // Subtract the track's inline padding: offsetLeft already includes it, so scrolling to
    // the raw difference pushes the first slide flush against the edge and swallows the
    // left gutter. Clamped at 0 so index 0 keeps the padding visible.
    const padStart = parseFloat(getComputedStyle(track).paddingInlineStart) || 0;
    track.scrollTo({
      left: Math.max(0, target.offsetLeft - track.offsetLeft - padStart),
      behavior: this.#reducedMotion ? 'auto' : 'smooth',
    });
    this.#syncControls();
  }

  /** @param {number} direction */
  #step(direction) {
    const loop = this.dataset.loop === 'true';
    let next = this.#index + direction;
    if (next < 0) next = loop ? this.#pageCount - 1 : 0;
    if (next > this.#pageCount - 1) next = loop ? 0 : this.#pageCount - 1;
    this.#goTo(next);
  }

  #onPrev = () => this.#step(-1);
  #onNext = () => this.#step(1);

  /* ---------- autoplay ---------- */

  #startAutoplay() {
    if (!this.#autoplayMs || this.#reducedMotion || this.#paused) return;
    this.#stopAutoplay();
    this.#timer = setInterval(() => {
      if (this.#maxScroll <= 1) return;
      const track = this.refs.track;
      const atEnd = track ? track.scrollLeft >= this.#maxScroll - 1 : false;
      this.#goTo(atEnd ? 0 : this.#index + 1);
    }, this.#autoplayMs);
  }

  #stopAutoplay() {
    if (this.#timer) clearInterval(this.#timer);
    this.#timer = 0;
  }

  #suspend = () => this.#stopAutoplay();

  #resume = () => {
    if (!this.#paused) this.#startAutoplay();
  };

  #onVisibilityChange = () => {
    if (document.hidden) this.#stopAutoplay();
    else this.#resume();
  };

  #togglePause = () => {
    const button = this.refs.pause;
    if (!button) return;

    this.#paused = !this.#paused;
    if (this.#paused) this.#stopAutoplay();
    else this.#startAutoplay();

    button.setAttribute('aria-pressed', String(this.#paused));
    const label = this.#paused ? button.dataset.labelPlay : button.dataset.labelPause;
    if (label) button.setAttribute('aria-label', label);
    button.dataset.state = this.#paused ? 'paused' : 'playing';
  };
}

if (!customElements.get('sf-ab-slider')) {
  customElements.define('sf-ab-slider', SfAbSlider);
}
